import { Trans, useLingui } from '@lingui/react/macro';
import { Dialog, DialogContent, DialogHeader, DialogTitle, cn } from '@pantry/ui';
import type { ProductStatsItem } from '@pantry/api-client';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import {
  ArchiveBoxIcon,
  ShoppingCartIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface ProductStatsDialogProps {
  product: ProductStatsItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog showing detailed statistics for a product.
 */
export function ProductStatsDialog({ product, open, onOpenChange }: ProductStatsDialogProps) {
  const { t, i18n } = useLingui();
  const currencyFormatter = new Intl.NumberFormat(i18n.locale, {
    style: 'currency',
    currency: 'USD',
  });

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'fruits_vegetables':
        return t`Fruits & Vegetables`;
      case 'dairy_products':
        return t`Dairy Products`;
      case 'meat_poultry':
        return t`Meat & Poultry`;
      case 'seafood':
        return t`Seafood`;
      case 'bakery':
        return t`Bakery`;
      case 'beverages':
        return t`Beverages`;
      case 'snacks':
        return t`Snacks`;
      case 'frozen_foods':
        return t`Frozen Foods`;
      case 'canned_goods':
        return t`Canned Goods`;
      case 'condiments_sauces':
        return t`Condiments & Sauces`;
      case 'grains_pasta':
        return t`Grains & Pasta`;
      case 'breakfast_cereals':
        return t`Breakfast & Cereals`;
      case 'household_items':
        return t`Household Items`;
      case 'personal_care':
        return t`Personal Care`;
      case 'baby_products':
        return t`Baby Products`;
      case 'pet_supplies':
        return t`Pet Supplies`;
      case 'other':
        return t`Other`;
      default:
        return category;
    }
  };
  const resolveProductName = useResolveProductName();

  if (!product) return null;

  // Resolve localized product name
  const displayName = resolveProductName({
    name: product.productName,
    names: product.productNames ?? undefined,
  });

  const totalUsed = product.totalConsumed + product.totalWasted;
  const consumedPercentage = totalUsed > 0 ? (product.totalConsumed / totalUsed) * 100 : 100;
  const wastedPercentage = totalUsed > 0 ? (product.totalWasted / totalUsed) * 100 : 0;

  const wasteRateColor =
    product.wasteRate > 30
      ? 'text-expired'
      : product.wasteRate > 15
        ? 'text-expiring-soon'
        : 'text-fresh';

  // Filter to only show waste reasons with counts > 0
  const wasteReasons = Object.entries(product.wasteReasons)
    .filter(([, count]) => count > 0)
    .map(([reason, count]) => ({
      reason: formatWasteReason(reason, t),
      count,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-4">
            {/* Product image */}
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-muted overflow-hidden">
              {product.productImageUrl ? (
                <img
                  src={product.productImageUrl}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ArchiveBoxIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display text-xl truncate">{displayName}</DialogTitle>
              {product.productBrand && (
                <p className="text-sm text-muted-foreground">{product.productBrand}</p>
              )}
              {product.productCategory && (
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                  {getCategoryLabel(product.productCategory)}
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <MetricCard
            label={t`In Stock`}
            value={product.currentStock}
            icon={ArchiveBoxIcon}
            color="primary"
          />
          <MetricCard label={t`Purchased`} value={product.totalPurchased} icon={ShoppingCartIcon} />
          <MetricCard
            label={t`Spent`}
            value={
              product.totalSpent > 0 ? currencyFormatter.format(product.totalSpent / 100) : '-'
            }
            icon={CurrencyDollarIcon}
          />
        </div>

        {/* Consumption Breakdown */}
        <div className="mt-6">
          <h3 className="font-display font-semibold text-foreground mb-3">
            <Trans>Consumption Breakdown</Trans>
          </h3>

          {totalUsed > 0 ? (
            <>
              {/* Visual bar */}
              <div className="h-6 rounded-lg overflow-hidden flex">
                <div
                  className="bg-fresh flex items-center justify-center text-xs font-medium text-white transition-all duration-500"
                  style={{ width: `${consumedPercentage}%` }}
                >
                  {consumedPercentage > 15 && t`${product.totalConsumed} consumed`}
                </div>
                <div
                  className="bg-expired flex items-center justify-center text-xs font-medium text-white transition-all duration-500"
                  style={{ width: `${wastedPercentage}%` }}
                >
                  {wastedPercentage > 15 && t`${product.totalWasted} wasted`}
                </div>
              </div>

              {/* Legend */}
              <div className="flex justify-between mt-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-fresh" />
                  <span className="text-muted-foreground">
                    <Trans>
                      Consumed: {product.totalConsumed} ({consumedPercentage.toFixed(0)}%)
                    </Trans>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-expired" />
                  <span className="text-muted-foreground">
                    <Trans>
                      Wasted: {product.totalWasted} ({wastedPercentage.toFixed(0)}%)
                    </Trans>
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-4 bg-muted/30 rounded-lg">
              <p className="text-muted-foreground text-sm">
                <Trans>No consumption data yet</Trans>
              </p>
            </div>
          )}
        </div>

        {/* Waste Reasons */}
        {wasteReasons.length > 0 && (
          <div className="mt-6">
            <h3 className="font-display font-semibold text-foreground mb-3">
              <Trans>Waste Reasons</Trans>
            </h3>
            <div className="flex flex-wrap gap-2">
              {wasteReasons.map(({ reason, count }) => (
                <span
                  key={reason}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-expired/10 text-expired"
                >
                  {reason}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Additional Stats */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-4">
            {/* Average time to consume */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                <ClockIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  <Trans>Avg. Time to Consume</Trans>
                </p>
                <p className="font-medium text-foreground">
                  {product.avgTimeToConsumeDays !== null
                    ? t`${product.avgTimeToConsumeDays.toFixed(1)} days`
                    : '-'}
                </p>
              </div>
            </div>

            {/* Average price */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                <CurrencyDollarIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  <Trans>Avg. Price/Unit</Trans>
                </p>
                <p className="font-medium text-foreground">
                  {product.avgPricePerUnit !== null
                    ? currencyFormatter.format(product.avgPricePerUnit / 100)
                    : '-'}
                </p>
              </div>
            </div>

            {/* Last purchase date */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                <CalendarDaysIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  <Trans>Last Purchased</Trans>
                </p>
                <p className="font-medium text-foreground">
                  {product.lastPurchaseDate
                    ? format(new Date(product.lastPurchaseDate), 'MMM d, yyyy')
                    : '-'}
                </p>
              </div>
            </div>

            {/* Waste rate */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                <XMarkIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  <Trans>Waste Rate</Trans>
                </p>
                <p className={cn('font-medium', wasteRateColor)}>{product.wasteRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Metric card for key statistics
 */
function MetricCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'primary';
}) {
  return (
    <div className="text-center p-3 rounded-xl bg-muted/30">
      <div
        className={cn(
          'mx-auto flex h-10 w-10 items-center justify-center rounded-lg mb-2',
          color === 'primary' ? 'bg-primary/10' : 'bg-muted'
        )}
      >
        <Icon
          className={cn('h-5 w-5', color === 'primary' ? 'text-primary' : 'text-muted-foreground')}
        />
      </div>
      <p className="text-lg font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * Format waste reason for display
 */
function formatWasteReason(reason: string, t?: ReturnType<typeof useLingui>['t']): string {
  if (!t) {
    const labels: Record<string, string> = {
      expired: 'Expired',
      spoiled: 'Spoiled',
      damaged: 'Damaged',
      disliked: 'Disliked',
      overstock: 'Overstock',
      other: 'Other',
    };
    return labels[reason] || reason;
  }

  switch (reason) {
    case 'expired':
      return t`Expired`;
    case 'spoiled':
      return t`Spoiled`;
    case 'damaged':
      return t`Damaged`;
    case 'disliked':
      return t`Disliked`;
    case 'overstock':
      return t`Overstock`;
    case 'other':
      return t`Other`;
    default:
      return reason;
  }
}
